import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import useAuthStore from '@/stores/authStore';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiresSubscription?: boolean;
  requiresEmailVerification?: boolean;
}

export default function ProtectedRoute({ 
  children, 
  requiresSubscription = false,
  requiresEmailVerification = false 
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, organization, isEmailVerified, user } = useAuthStore();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check email verification requirement
  if (requiresEmailVerification && !isEmailVerified) {
    // Allow access to email verification page itself
    if (location.pathname !== '/email-verification') {
      return <Navigate to="/email-verification" state={{ from: location }} replace />;
    }
  }

  // Check if user should see welcome page
  const isWelcome = searchParams.get('welcome') === 'true';
  const isPaymentSuccess = searchParams.get('payment') === 'success';
  
  // If coming from signup or payment success, show welcome page
  if ((isWelcome || isPaymentSuccess) && location.pathname !== '/welcome') {
    const welcomeUrl = `/welcome?${searchParams.toString()}`;
    return <Navigate to={welcomeUrl} replace />;
  }

  // For subscription-required routes, check trial/payment status
  if (requiresSubscription && organization) {
    const subscription = (organization as any).billing?.subscriptions?.riderelease || (organization as any).subscription;
    const status = String(subscription?.status || '').toLowerCase();
    const hasAccess = status === 'active' || status === 'trial' || status === 'trialing';

    // Admin bypass — must match AuthenticatedRoute bypass list
    const BYPASS_EMAILS = ['sophie@quarryside.com', 'sophie@quarrygrove.com'];
    const userEmail = String((user as any)?.email || '').toLowerCase();
    const bypass = userEmail && BYPASS_EMAILS.includes(userEmail);

    // Check for recent checkout success
    let recentCheckoutSuccess = false;
    try {
      const ts = Number(localStorage.getItem('rr_last_checkout_success_at') || '0');
      recentCheckoutSuccess = ts > 0 && (Date.now() - ts) < 60 * 60 * 1000;
    } catch {}

    // If no subscription and not in trial and not bypassed, redirect to upgrade
    if (!hasAccess && !bypass && !recentCheckoutSuccess) {
      return <Navigate to="/settings?tab=billing&upgrade=required" replace />;
    }
  }

  return <>{children}</>;
}